import type { Checkin } from '@awwyeah/shared';
import { seenByViewers } from '@awwyeah/shared';
import type { MemberDirectory } from '../members';
import { Avatar } from './Avatar';

/**
 * "Seen by" — the only social signal in the whole module.
 *
 * The design's rule, verbatim: *"'Seen by' is a fact, not a score, and it never shows on
 * your own unseen posts as a nag."* So:
 *
 *  - faces, never a number (past three, it collapses to "everyone" — still not a count);
 *  - nothing at all until at least one other member has actually seen it, which means the
 *    author of a fresh post sees empty space rather than "Seen by 0";
 *  - the author is never in their own row.
 */
export function SeenByRow({
  checkin,
  members,
  memberCount,
}: {
  checkin: Checkin;
  members: MemberDirectory;
  /** How many people are in the household, so "everyone" can be said honestly. */
  memberCount: number;
}): JSX.Element | null {
  const viewers = seenByViewers(checkin);
  if (viewers.length === 0) return null;

  const everyoneElse = memberCount > 1 && viewers.length >= memberCount - 1;

  return (
    <div className="flex items-center gap-1.5" data-testid="checkin-seen-by">
      <span className="text-xs text-fg-3">{everyoneElse ? 'Seen by everyone' : 'Seen by'}</span>
      {!everyoneElse && viewers.map((id) => <Avatar key={id} member={members.get(id)} size={20} />)}
    </div>
  );
}
